import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { NCCC_LOGO, HERO_IMAGE } from "../lib/assets";
import NewsletterForm from "../components/NewsletterForm";

const PILLARS = [
  {
    n: "01",
    title: "Music & Arts Platform",
    text: "Independent record label and broader arts vessel — releasing emerging talent rooted in Sheffield and a wider Northern network.",
    testid: "pillar-music",
  },
  {
    n: "02",
    title: "Cultural Representation & Regional Outreach",
    text: "Amplifying local and Northern voices. Inclusive collaboration with LGBTQI+ and migrant communities. Regional synergy across cities.",
    testid: "pillar-culture",
  },
  {
    n: "03",
    title: "Strengthening Northern Identity",
    text: "Aligned with Sheffield's Culture Strategy 2035. Igniting the foundations for culture to thrive — a unified Northern front on a national stage.",
    testid: "pillar-identity",
  },
];

const MANIFESTO_PARAS = [
  "We are all living, and suffering in, a post-affect society. A new dividualism has made us all abstract general entities — interchangeable, distinguished only by the symbolic, classifying language the cultural hegemony allows us for labelling our identities.",
  "We have all suffered under the Western myth of the autonomous individual subject. For too long, our grand narratives have been shaped by the categorisation, classification and division that is the mode of the colonialist project.",
  "The computer has become a metaphor for the mind, but the language of the algorithm is always binary. Life itself is analogue; infinite.",
  "Resistance is within us, and it is multiple and multiplying. Resistance is a meme.",
  "We are building an emancipatory politics, drawing on different stories of the human — a new emancipatory metaphysics expressed through multiple modes. A new collaborative culture of affect.",
];

export default function HomePage() {
  return (
    <div className="relative z-10">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
      >
        <div className="lg:col-span-7 warm-up">
          <span className="label-accent">// Transmission 001 — Sheffield, North</span>
          <h1
            className="broadcast-headline text-5xl sm:text-6xl lg:text-7xl mt-6 chromatic"
            data-testid="home-title"
          >
            A new counter
            <br />
            cultural resistance.
          </h1>
          <p className="mt-8 max-w-xl text-[color:var(--nccc-muted)] text-sm leading-relaxed">
            New Counter Culture Collective is a Sheffield-based record label and
            cultural collective. We are building a collaborative culture of
            <span className="text-[color:var(--nccc-ochre)]"> affect</span> —
            against the binary, against the dividual, in service of the
            analogue and the infinite.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/releases" className="nccc-btn" data-testid="home-cta-releases">
              See releases <ArrowRight size={16} />
            </Link>
            <a
              href="https://nccc.sumupstore.com/"
              target="_blank"
              rel="noreferrer"
              className="nccc-btn"
              data-testid="home-cta-store"
            >
              Visit the store <ArrowRight size={16} />
            </a>
            <Link to="/submit" className="nccc-btn nccc-btn-ghost" data-testid="home-cta-submit">
              Submit a transmission
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 relative warm-up-2">
          <div className="relative aspect-square mx-auto max-w-[460px]">
            <img
              src={NCCC_LOGO}
              alt="NCCC logo"
              data-testid="home-hex-logo"
              className="absolute inset-0 w-full h-full object-contain"
              style={{
                clipPath:
                  "polygon(50% 8%, 88% 30%, 88% 70%, 50% 92%, 12% 70%, 12% 30%)",
                filter: "drop-shadow(8px 10px 0 var(--nccc-ink))",
              }}
            />
          </div>
          <div className="absolute -bottom-3 left-0 right-0 flex justify-between label">
            <span>NCCC // IDENT</span>
            <span>EST. SHEFFIELD</span>
          </div>
        </div>
      </section>

      <hr className="transmission-rule max-w-[1400px] mx-auto" />

      {/* MANIFESTO */}
      <section
        data-testid="manifesto-section"
        className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12"
      >
        <div className="lg:col-span-4">
          <span className="label-accent">// 002 — Manifesto</span>
          <h2 className="broadcast-headline text-4xl lg:text-5xl mt-5 text-[color:var(--nccc-cream)]">
            We are waking
            <br />
            up from the
            <br />
            collective
            <br />
            bad dream.
          </h2>
          <p className="mt-6 label">— after Mark Fisher</p>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-6">
          {MANIFESTO_PARAS.map((p, i) => (
            <p
              key={i}
              className="text-[color:var(--nccc-cream)] text-base sm:text-[17px] leading-relaxed font-mono"
              style={{ opacity: 0.92 }}
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      <hr className="transmission-rule max-w-[1400px] mx-auto" />

      {/* PILLARS — three hex tiles */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div>
            <span className="label-accent">// 003 — Pillars</span>
            <h2 className="broadcast-headline text-4xl lg:text-5xl mt-3 text-[color:var(--nccc-cream)]">
              Three modes of operation.
            </h2>
          </div>
          <p className="label max-w-md">
            Encoded as a triplet. Each hex a node in a wider Northern mesh.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, idx) => (
            <div
              key={p.n}
              className="hex-card relative warm-up"
              style={{ animationDelay: `${idx * 120}ms`, aspectRatio: "1 / 1" }}
              data-testid={p.testid}
            >
              <div className="hex-inner" />
              <div className="absolute inset-0 z-[2] flex flex-col justify-center items-start p-8 sm:p-10">
                <span className="label-accent">{p.n}</span>
                <h3 className="font-display text-2xl md:text-3xl text-[color:var(--nccc-cream)] mt-3 leading-tight">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm text-[color:var(--nccc-muted)] leading-relaxed">
                  {p.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <hr className="transmission-rule max-w-[1400px] mx-auto" />

      {/* VIDEO TRANSMISSION */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="label-accent">// 004 — Transmission</span>
          <h2 className="broadcast-headline text-4xl lg:text-5xl mt-4 text-[color:var(--nccc-ink)]">
            From the channel.
          </h2>
          <p className="mt-6 text-[color:var(--nccc-muted)]">
            A signal from the NCCC YouTube channel — sessions, drops, talks. Press play.
          </p>
          <a
            href="https://www.youtube.com/@NCCCollective"
            target="_blank"
            rel="noreferrer"
            className="nccc-btn nccc-btn-ghost mt-8 inline-flex"
            data-testid="home-channel-link"
          >
            See the full channel ↗
          </a>
        </div>
        <div className="lg:col-span-7">
          <div
            className="relative aspect-video border border-[color:var(--nccc-border)]"
            style={{ boxShadow: "8px 8px 0 0 var(--nccc-ink)" }}
            data-testid="home-video-embed"
          >
            <iframe
              src="https://www.youtube.com/embed/-AbNHIqQJSY"
              title="NCCC transmission"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      </section>

      <hr className="transmission-rule max-w-[1400px] mx-auto" />

      {/* DESIRE / CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <span className="label-accent">// 005 — Open call</span>
          <h2 className="broadcast-headline text-4xl lg:text-6xl mt-4 text-[color:var(--nccc-cream)]">
            Our collective desire is now a desire for life.
          </h2>
          <p className="mt-6 text-[color:var(--nccc-muted)] max-w-2xl">
            Artists, labels, venues, zines, broadcasters — if you recognise the
            signal, send us yours. We are actively seeking contributors and
            collaborators across the North.
          </p>
        </div>
        <div className="lg:col-span-4 flex items-end">
          <Link to="/submit" className="nccc-btn w-full justify-center" data-testid="home-cta-submit-2">
            Send transmission <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <hr className="transmission-rule max-w-[1400px] mx-auto" />

      {/* NEWSLETTER */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="label-accent">// 006 — Signal</span>
          <h2 className="broadcast-headline text-4xl lg:text-5xl mt-4 text-[color:var(--nccc-cream)]">
            Join the signal.
          </h2>
          <p className="mt-6 text-[color:var(--nccc-muted)]">
            One transmission per release. No noise. Unsubscribe at any time —
            but you won&apos;t want to.
          </p>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 self-end">
          <NewsletterForm source="home" testid="home-newsletter" />
          <p className="label mt-4">// we will never share your address with the algorithm.</p>
        </div>
      </section>
    </div>
  );
}
